import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { alpha } from "@mui/material/styles";
import OnboardProductDialog from "./OnboardProductDialog";
import { useGetUsersQuery, useToggleUserMutation } from "../../../app/services/user";
import { useGetProductUsersQuery } from "../../../app/services/userProduct";
import { useGetUserRolesQuery } from "../../../app/services/userRole";
import DealerRolesTab from "./roles/DealerRolesTab";
import { useLazyGetRolesQuery } from "../../../app/services/roles";
import {
  useGetDealerDetailsQuery,
  useGetDealerSubscriptionHistoryQuery,
  useGetDealerLocationsQuery,
} from "../../../app/services/dealer";
import { formatDateToLocalTime } from "../../../util/formatDate";
import { getApiErrorMessage } from "../../../util/getApiErrorMessage";
import type { DealerSubscriptionHistoryDto } from "../../models/dealer-subscription";
import { useGetDealerActiveAddOnsQuery } from "../../../app/services/addons";

// Remove GUIDs from display text, and clean up leftover parentheses/spaces
const sanitizeReason = (reason?: string): string => {
  if (!reason) return "-";
  const cleaned = reason
    .replace(/\b[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\b/g, "")
    .replace(/\(\s*\)/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();
  return cleaned || "-";
};

interface DealerDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  dealerId?: number | null;
  dealerName?: string;
}

const InfoRow = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <Grid container spacing={1} sx={{ mb: 0.5 }}>
    <Grid item xs={5} sm={4} md={3} lg={3}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Grid>
    <Grid item xs={7} sm={8} md={9} lg={9}>
      <Typography variant="body2">{value ?? "-"}</Typography>
    </Grid>
  </Grid>
);

const DealerDetailsDialog: React.FC<DealerDetailsDialogProps> = ({
  open,
  onClose,
  dealerId,
  dealerName,
}) => {
  const skip = !open || !dealerId;

  const {
    data: details,
    isFetching: detailsLoading,
    isError: detailsIsError,
    error: detailsError,
    refetch: refetchDetails,
  } = useGetDealerDetailsQuery(dealerId as number, { skip });

  const {
    data: history,
    isFetching: historyLoading,
    isError: historyIsError,
    error: historyError,
    refetch: refetchHistory,
  } = useGetDealerSubscriptionHistoryQuery(dealerId as number, { skip });

  const anyLoading = detailsLoading || historyLoading;
  const anyError = detailsIsError || historyIsError;

  const sortedHistory: DealerSubscriptionHistoryDto[] = React.useMemo(() => {
    if (!history) return [];
    return [...history].sort(
      (a, b) => new Date(b.recordedDate).getTime() - new Date(a.recordedDate).getTime()
    );
  }, [history]);

  const groupedHistory = React.useMemo(() => {
    const groups: Record<string, DealerSubscriptionHistoryDto[]> = {};
    for (const h of sortedHistory) {
      const key = h.productName || "Unknown Product";
      if (!groups[key]) groups[key] = [];
      groups[key].push(h);
    }
    return groups;
  }, [sortedHistory]);

  const [activeTab, setActiveTab] = React.useState<'subscriptions' | 'roles' | 'users' | 'locations'>('subscriptions');
  const [onboardOpen, setOnboardOpen] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [editProductId, setEditProductId] = React.useState<string | undefined>(undefined);
  const [editSubscriptionId, setEditSubscriptionId] = React.useState<string | undefined>(undefined);
  const assignedProductIds = React.useMemo(() => (details?.currentSubscriptions ?? []).map(s => s.productId), [details]);
  // For tab counts
  const subsCount = details?.currentSubscriptions?.length ?? 0;
  const { data: usersPage } = useGetUsersQuery(
    { dealerId: dealerId as number, page: 1, pageSize: 1 },
    { skip }
  );
  const usersCount = (usersPage as any)?.totalRecordsCount ?? 0;
  // Locations
  const { data: locations } = useGetDealerLocationsQuery(dealerId as number, { skip });
  const locationsCount = (locations?.length ?? 0);

  // Roles count across active products (Option A)
  const [triggerGetRoles] = useLazyGetRolesQuery();
  const [rolesCount, setRolesCount] = React.useState(0);
  React.useEffect(() => {
    const dmsDealerId = details?.dmsDealerId;
    const subs = details?.currentSubscriptions ?? [];
    if (!dmsDealerId || subs.length === 0 || skip) {
      setRolesCount(0);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const promises = subs.map((s) =>
          triggerGetRoles({ productId: s.productId, dmsDealerId, page: 1, pageSize: 200 }, true)
        );
        const results = await Promise.all(promises);
        const sum = results.reduce((acc, r) => {
          const payload: any = r.data as any;
          if (!payload) return acc;
          const list = Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : []);
          return acc + (list?.length ?? 0);
        }, 0);
        if (!cancelled) setRolesCount(sum);
      } catch {
        if (!cancelled) setRolesCount(0);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details?.dmsDealerId, details?.currentSubscriptions?.length, skip]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" scroll="paper"
      sx={{
        "& .MuiDialog-paper": {
          width: '100%',
          m: 0,
        }
      }}
    >
      <DialogTitle>
        Dealer Details{dealerName ? `: ${dealerName}` : ""}
      </DialogTitle>
      <DialogContent dividers sx={{ p: { xs: 1, sm: 2 } }}>
        {anyLoading ? (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 160 }}>
            <CircularProgress />
          </Box>
        ) : anyError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {getApiErrorMessage(detailsError || historyError, "Failed to load dealer details")}
          </Alert>
        ) : details ? (
          <Box>
            {/* Compact summary row to save space */}
            <Box sx={{ mb: 1 }}>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                <Typography variant="subtitle1" sx={{ mr: 1 }}>
                  {details.name}
                </Typography>
                <Chip size="small" label={details.dmsType} />
                <Chip size="small" label={`Everest Dealer Id: ${dealerId ?? '-'}`} variant="outlined" />
                <Chip size="small" label={`DMS Dealer Id: ${details.dmsDealerId ?? '-'}`} variant="outlined" />
                <Chip size="small" label={`Login System Id: ${details.loginSystemId ?? '-'}`} variant="outlined" />
                {details.servername && <Chip size="small" label={`Server: ${details.servername}`} variant="outlined" />}
                <Chip size="small" label={details.isActive ? "Active" : "Inactive"} color={details.isActive ? "success" : "default"} />
                {details.isTest && <Chip size="small" label="Test" color="warning" />}
              </Stack>
            </Box>

            {/* Collapsible detailed Basic Information */}
            <Accordion sx={{ mb: 1 }} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="basic-info-content" id="basic-info-header">
                <Typography variant="subtitle2">Basic Information</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <InfoRow label="Name" value={details.name} />
                <InfoRow label="DMS Type" value={details.dmsType} />
                <InfoRow label="DMS Dealer ID" value={details.dmsDealerId ?? "-"} />
                <InfoRow label="DMS Location ID" value={details.dmsLocationId} />
                <InfoRow label="Server Name" value={details.servername ?? "-"} />
                <InfoRow
                  label="Status"
                  value={
                    <Stack direction="row" spacing={1}>
                      <Chip size="small" label={details.isActive ? "Active" : "Inactive"} color={details.isActive ? "success" : "default"} />
                      {details.isTest && <Chip size="small" label="Test" color="warning" />}
                    </Stack>
                  }
                />
                <InfoRow
                  label="Address"
                  value={
                    [details.address, details.city, details.stateOrProvince, details.zipPostal]
                      .filter(Boolean)
                      .join(", ") || "-"
                  }
                />
              </AccordionDetails>
            </Accordion>

            <Tabs
              value={activeTab}
              onChange={(_e, val) => setActiveTab(val)}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label={`Subscriptions (${subsCount})`} value="subscriptions" />
              <Tab label={`Roles (${rolesCount})`} value="roles" />
              <Tab label={`Users (${usersCount})`} value="users" />
              <Tab label={`Locations (${locationsCount})`} value="locations" />
            </Tabs>

            <Box sx={{ mt: 2 }}>
              {activeTab === 'subscriptions' && (
                <Box>
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                      Current Subscriptions
                    </Typography>
                    <Button variant="contained" size="small" onClick={() => setOnboardOpen(true)}>
                      Onboard Product
                    </Button>
                  </Box>
                  {details.currentSubscriptions?.length ? (
                    <Box sx={{ width: '100%', overflowX: 'auto' }}>
                      <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Subscription</TableCell>
                          <TableCell>Product</TableCell>
                          <TableCell>Start Date</TableCell>
                          <TableCell align="center">Add-ons</TableCell>
                          <TableCell align="center">Users</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {details.currentSubscriptions.map((s) => {
                          const isLegacy = String(s.subscriptionId) === '00000000-0000-0000-0000-000000000000';
                          return (
                          <TableRow key={`${s.subscriptionId}-${s.productId}`} hover>
                            <TableCell>
                              {s.subscriptionName}
                              {isLegacy && (
                                <>
                                  <Chip size="small" label="Legacy" color="warning" variant="outlined" sx={{ ml: 1 }} />
                                  <Tooltip
                                    title="Legacy Assignment: Dealer is assigned without a current subscription tier mapping. Use Set Tier to move to a supported tier."
                                    placement="top"
                                  >
                                    <InfoOutlinedIcon fontSize="small" sx={{ ml: 0.5, color: 'text.secondary', verticalAlign: 'middle' }} />
                                  </Tooltip>
                                </>
                              )}
                            </TableCell>
                            <TableCell>{s.productName}</TableCell>
                            <TableCell>{formatDateToLocalTime(s.startDate)}</TableCell>
                            <TableCell align="center">
                              <AddOnCount dealerId={dealerId ?? undefined} productId={s.productId} />
                            </TableCell>
                            <TableCell align="center">
                              <ProductUsersCount dealerId={dealerId ?? undefined} productId={s.productId} />
                            </TableCell>
                            <TableCell align="center">
                              {isLegacy ? (
                                <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => {
                                      setEditProductId(String(s.productId));
                                      setEditSubscriptionId(undefined);
                                      setEditOpen(true);
                                    }}
                                    sx={{ textTransform: 'none' }}
                                  >
                                    Set Tier
                                  </Button>
                                  <Tooltip
                                    title="Assign this dealer to a current subscription tier for the selected product."
                                    placement="top"
                                  >
                                    <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                  </Tooltip>
                                </Stack>
                              ) : (
                                <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => {
                                      setEditProductId(String(s.productId));
                                      setEditSubscriptionId(String(s.subscriptionId));
                                      setEditOpen(true);
                                    }}
                                    sx={{ textTransform: 'none' }}
                                  >
                                    Edit Tier
                                  </Button>
                                  <Tooltip
                                    title="Edit Tier: Change the assigned subscription tier for this dealer and product."
                                    placement="top"
                                  >
                                    <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                  </Tooltip>
                                </Stack>
                              )}
                            </TableCell>
                          </TableRow>
                        );})}
                      </TableBody>
                      </Table>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No current subscriptions
                    </Typography>
                  )}
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Subscription History
                    </Typography>
                    {sortedHistory.length ? (
                      <Box sx={{ width: '100%', overflowX: 'auto' }}>
                        {Object.entries(groupedHistory).map(([product, rows]) => (
                          <Accordion key={product} disableGutters>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="subtitle2">{product} ({rows.length})</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Table size="small" stickyHeader={false}>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Recorded</TableCell>
                                    <TableCell>Action</TableCell>
                                    <TableCell>Subscription</TableCell>
                                    <TableCell sx={{ minWidth: 200, whiteSpace: 'nowrap' }}>Period</TableCell>
                                    <TableCell sx={{ width: 160, maxWidth: 160 }}>By</TableCell>
                                    <TableCell sx={{ width: 300, maxWidth: 300 }}>Reason</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {rows.map((h) => {
                                    const period = `${formatDateToLocalTime(h.startDate)} --> ${h.endDate ? formatDateToLocalTime(h.endDate) : 'Ongoing'}`;
                                    const sanitized = sanitizeReason(h.reason ?? undefined);
                                    return (
                                      <TableRow
                                        key={`${h.subscriptionId}-${h.recordedDate}`}
                                        sx={h.isActive ? {
                                          backgroundColor: (theme) => alpha(theme.palette.success.light, 0.18)
                                        } : undefined}
                                      >
                                        <TableCell>{formatDateToLocalTime(h.recordedDate)}</TableCell>
                                        <TableCell>{h.actionType}</TableCell>
                                        <TableCell>{h.subscriptionName}</TableCell>
                                        <TableCell sx={{ minWidth: 200, whiteSpace: 'nowrap' }}>{period}</TableCell>
                                        <TableCell sx={{ width: 160, maxWidth: 160 }}>
                                          <Tooltip title={h.actionBy ?? '-'} placement="top" arrow>
                                            <Typography variant="body2" noWrap sx={{ maxWidth: 140 }}>
                                              {h.actionBy ?? '-'}
                                            </Typography>
                                          </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ width: 300, maxWidth: 300 }}>
                                          <Tooltip title={sanitized} placement="top" arrow>
                                            <Typography variant="body2" noWrap sx={{ maxWidth: 280 }}>
                                              {sanitized}
                                            </Typography>
                                          </Tooltip>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No subscription history
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {activeTab === 'roles' && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Roles
                  </Typography>
                  {details ? (
                    <DealerRolesTab
                      dealerId={dealerId as number}
                      dmsDealerId={details.loginSystemId ?? undefined}
                      subscriptions={details.currentSubscriptions}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">No dealer details loaded.</Typography>
                  )}
                </Box>
              )}

              {activeTab === 'users' && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Users
                  </Typography>
                  {dealerId ? (
                    <DealerUsersTable dealerId={dealerId} />
                  ) : (
                    <Typography variant="body2" color="text.secondary">No dealer selected</Typography>
                  )}
                </Box>
              )}

              {activeTab === 'locations' && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Locations
                  </Typography>
                  {!locations || locations.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No locations found for this dealer.</Typography>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell width={140}>Dealer ID</TableCell>
                          <TableCell>Location</TableCell>
                          <TableCell>Server</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {locations.map((loc) => (
                          <TableRow key={loc.dealerId}>
                            <TableCell>
                              <Typography variant="body2">{loc.dealerId}</Typography>
                              {loc.isRoot && <Chip size="small" color="primary" label="Root" sx={{ ml: 1 }} />}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{loc.name ?? '-'}</Typography>
                              <Typography variant="caption" display="block" color="text.secondary">{loc.address ?? '-'}</Typography>
                            </TableCell>
                            <TableCell>{loc.server ?? '-'}</TableCell>
                            <TableCell>
                              <Chip size="small" label={loc.isActive ? 'Active' : 'Inactive'} color={loc.isActive ? 'success' : 'default'} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No details available
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">Close</Button>
      </DialogActions>

      {/* Onboard Product Dialog */}
      <OnboardProductDialog
        open={onboardOpen}
        onClose={() => setOnboardOpen(false)}
        dealerId={dealerId}
        dealerName={dealerName}
        assignedProductIds={assignedProductIds}
        onSuccess={() => { refetchDetails(); refetchHistory(); }}
      />

      {/* Edit Product Assignment Dialog */}
      <OnboardProductDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        dealerId={dealerId}
        dealerName={dealerName}
        assignedProductIds={assignedProductIds}
        initialSelectedProductId={editProductId}
        initialSelectedSubscriptionId={editSubscriptionId}
        lockProductSelection
        mode="edit"
        onSuccess={() => { refetchDetails(); refetchHistory(); }}
      />
    </Dialog>
  );
}

export default DealerDetailsDialog;

// Read-only users table for a dealer with search and unlock
const DealerUsersTable: React.FC<{ dealerId: number }> = ({ dealerId }) => {
  const [searchInput, setSearchInput] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState<string | undefined>(undefined);
  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput || undefined), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isFetching, isError, refetch } = useGetUsersQuery(
    { dealerId, page: 1, pageSize: 100, generalSearchText: debouncedSearch },
    { refetchOnMountOrArgChange: true }
  );
  const users = (data?.data as UserInformation[]) ?? [];
  const [toggleUser, { isLoading: unlocking }] = useToggleUserMutation();
  const [unlockingId, setUnlockingId] = React.useState<string | null>(null);
  const [optimisticallyUnlocked, setOptimisticallyUnlocked] = React.useState<Record<string, boolean>>({});
  const [snack, setSnack] = React.useState<{ open: boolean; msg: string; severity: 'success' | 'error' }>({ open: false, msg: '', severity: 'success' });

  const formatLastLogin = (d?: string | null) => {
    if (!d) return "-";
    if (d.startsWith("0001-01-01")) return "-";
    return formatDateToLocalTime(d);
  };

  return (
    <Box>
      <Box sx={{ mb: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
        <input
          type="text"
          value={searchInput}
          placeholder="Search by username, name or email"
          onChange={(e) => setSearchInput(e.target.value)}
          style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid rgba(0,0,0,0.23)' }}
          autoFocus
        />
        {isFetching && <CircularProgress size={18} />}
      </Box>
      {isError && (
        <Alert severity="error" sx={{ mb: 1 }}>Failed to load users.</Alert>
      )}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>User Name</TableCell>
            <TableCell>Full Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Phone</TableCell>
            <TableCell>Roles</TableCell>
            <TableCell>Lockout</TableCell>
            <TableCell>DMS Admin</TableCell>
            <TableCell>Last Login</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
        {users.length === 0 ? (
          <TableRow>
            <TableCell colSpan={8}>
              <Typography variant="body2" color="text.secondary">No users found for this dealer.</Typography>
            </TableCell>
          </TableRow>
        ) : (
          users.map((u) => (
            <TableRow key={u.id as string} hover>
              <TableCell>{u.userName}</TableCell>
              <TableCell>{u.fullName ?? '-'}</TableCell>
              <TableCell>{u.email ?? '-'}</TableCell>
              <TableCell>{u.phoneNumber ?? '-'}</TableCell>
              <TableCell>
                <UserRolesBadge userId={String(u.id)} />
              </TableCell>
              <TableCell>
                {(u.lockoutEnabled && !optimisticallyUnlocked[String(u.id)]) ? (
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={unlocking || unlockingId === String(u.id)}
                    onClick={async () => {
                      setUnlockingId(String(u.id));
                      try {
                        await toggleUser({ userId: [String(u.id)], userToggleState: false }).unwrap();
                        setOptimisticallyUnlocked((prev) => ({ ...prev, [String(u.id)]: true }));
                        setSnack({ open: true, msg: `Unlocked ${u.userName}`, severity: 'success' });
                        // background refetch to sync fresh state
                        refetch();
                      } catch (e) {
                        setSnack({ open: true, msg: 'Failed to unlock user', severity: 'error' });
                      }
                      setUnlockingId(null);
                    }}
                  >
                    Unlock
                  </Button>
                ) : (
                  'No'
                )}
              </TableCell>
              <TableCell>{u.isDmsAdmin ? 'Yes' : 'No'}</TableCell>
              <TableCell>{formatLastLogin(u.lastLoginTime)}</TableCell>
            </TableRow>
          ))
        )}
        </TableBody>
      </Table>
      {snack.open && (
        <Alert sx={{ mt: 1 }} severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>
          {snack.msg}
        </Alert>
      )}
    </Box>
  );
};

// Badge for role count with tooltip of role names
const UserRolesBadge: React.FC<{ userId: string }> = ({ userId }) => {
  const { data: roles, isFetching, isError } = useGetUserRolesQuery(userId);
  if (isFetching) return <Chip size="small" label="…" variant="outlined" />;
  if (isError) return <Chip size="small" label="-" variant="outlined" />;
  const list = (roles ?? []) as CreateRoleResponse[];
  const count = list.length;
  const names = count ? list.map(r => r.name).join(', ') : '-';
  return (
    <Tooltip title={names} placement="top" arrow>
      <Chip size="small" label={String(count)} color="primary" variant="outlined" />
    </Tooltip>
  );
};

// Small helper to show add-on count for a dealer+product
const AddOnCount: React.FC<{ dealerId?: number; productId: string }> = ({ dealerId, productId }) => {
  const { data, isFetching } = useGetDealerActiveAddOnsQuery(
    { dealerId: dealerId as number, productId },
    { skip: !dealerId || !productId }
  );
  if (isFetching) return <Chip size="small" label="…" variant="outlined" />;
  return <Chip size="small" label={(data?.activeFeatureIds?.length ?? 0).toString()} color="primary" variant="outlined" />;
};

// Small helper to show total users assigned to any role for a dealer+product
const ProductUsersCount: React.FC<{ dealerId?: number; productId: string }> = ({ dealerId, productId }) => {
  const { data, isFetching, isError } = useGetProductUsersQuery(
    { productId, page: 1, pageSize: 1, dealerId, filterType: 'All', searchText: undefined },
    { skip: !dealerId || !productId }
  );
  if (isFetching) return <Chip size="small" label="…" variant="outlined" />;
  if (isError) return <Chip size="small" label="-" variant="outlined" />;
  const count = (data?.rowCount as number) ?? (data?.RowCount as number) ?? 0;
  return <Chip size="small" label={count.toString()} color="primary" variant="outlined" />;
};
